import React from 'react';

/**
 * Root-level Error Boundary.
 *
 * In React 19 concurrent rendering, any uncaught error in a child component
 * (render, lifecycle, or useEffect) unmounts the entire tree, leaving the
 * user staring at a blank white page. Wrapping <App /> in this boundary
 * means at worst the user sees a friendly fallback and can retry — never
 * a white screen.
 *
 * The error message is also surfaced (small, monospace) so production
 * support can ask the user to screenshot it.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[WAYO ErrorBoundary]', error, info?.componentStack);
    // Best-effort: ping the backend so we can correlate field reports.
    try {
      const payload = {
        event_name: 'app_error',
        params: {
          source: 'error_boundary',
          message: String(error?.message || error || 'unknown'),
          stack: String(error?.stack || '').slice(0, 1000),
          component_stack: String(info?.componentStack || '').slice(0, 1000),
          user_agent: navigator.userAgent,
          url: window.location.href,
        },
      };
      fetch(`${window.location.origin}/api/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    } catch (_) {
      /* noop */
    }
  }

  handleReload = () => {
    try {
      sessionStorage.removeItem('sw-reloaded');
    } catch (_) { /* noop */ }
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    const msg = String(this.state.error?.message || this.state.error || 'Unknown error');

    return (
      <div
        data-testid="root-error-boundary"
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          textAlign: 'center',
          background: '#fff',
          color: '#252525',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          zIndex: 9999,
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 16,
            background: '#FFD12D',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: 28,
            color: '#252525',
            marginBottom: 18,
          }}
        >
          !
        </div>
        <div style={{ maxWidth: 360, lineHeight: 1.5, fontSize: 15 }}>
          <strong>WAYO ran into a problem loading this page.</strong>
        </div>
        <div style={{ marginTop: 8, color: '#6b7280', fontSize: 13, maxWidth: 360 }}>
          We've logged the error. Please try again — if it keeps happening, take
          a screenshot of this screen and send it to support.
        </div>
        <button
          data-testid="error-boundary-reload"
          onClick={this.handleReload}
          style={{
            marginTop: 18,
            background: '#FFD12D',
            color: '#252525',
            padding: '10px 18px',
            borderRadius: 999,
            border: 'none',
            fontWeight: 600,
            fontSize: 15,
            cursor: 'pointer',
          }}
        >
          Try again
        </button>
        <pre
          style={{
            marginTop: 18,
            fontSize: 10,
            color: '#6b7280',
            opacity: 0.8,
            maxWidth: '90vw',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {msg}
        </pre>
      </div>
    );
  }
}
